'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

type SpinnerSize = 'sm' | 'md' | 'lg' | 'xl';
type SpinnerVariant = 'default' | 'primary' | 'secondary';

interface LoadingSpinnerProps {
  size?: SpinnerSize;
  variant?: SpinnerVariant;
  className?: string;
  label?: string;
  'data-testid'?: string;
}

const spinnerSizeStyles: Record<SpinnerSize, string> = {
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
  xl: 'w-12 h-12',
};

const spinnerVariantStyles: Record<SpinnerVariant, string> = {
  default: 'text-gray-600',
  primary: 'text-primary',
  secondary: 'text-secondary',
};

/**
 * Accessible loading spinner component with consistent animations
 * Supports multiple sizes and variants
 */
export function LoadingSpinner({
  size = 'md',
  variant = 'default',
  className = '',
  label = 'Loading...',
  'data-testid': testId,
}: LoadingSpinnerProps) {
  const sizeStyles = spinnerSizeStyles[size];
  const variantStyles = spinnerVariantStyles[variant];

  return (
    <motion.div
      className={cn('flex items-center justify-center', className)}
      data-testid={testId}
      role="status"
      aria-label={label}>
      <motion.div
        className={cn('border-2 border-current border-t-transparent rounded-full', sizeStyles, variantStyles)}
        animate={{ rotate: 360 }}
        transition={{
          duration: 1,
          repeat: Infinity,
          ease: 'linear',
        }}
      />
      <span className="sr-only">{label}</span>
    </motion.div>
  );
}

interface LoadingStateProps {
  isLoading: boolean;
  children: React.ReactNode;
  spinner?: React.ComponentProps<typeof LoadingSpinner>;
  fallback?: React.ReactNode;
}

/**
 * Wrapper component for conditional loading states
 * Shows spinner when loading, content when not loading
 */
export function LoadingState({ isLoading, children, spinner, fallback }: LoadingStateProps) {
  if (isLoading) {
    return fallback || <LoadingSpinner {...spinner} />;
  }

  return <>{children}</>;
}

interface InlineLoadingProps extends LoadingSpinnerProps {
  text?: string;
  textClassName?: string;
}

/**
 * Inline loading component with text
 * Useful for buttons and inline actions
 */
export function InlineLoading({ text = 'Loading...', textClassName = '', ...spinnerProps }: InlineLoadingProps) {
  return (
    <div className="flex items-center gap-2">
      <LoadingSpinner {...spinnerProps} />
      <span className={cn('text-sm text-muted-foreground', textClassName)}>{text}</span>
    </div>
  );
}
